import React, { useCallback, useRef } from 'react';
import { FormFieldProps } from '../Form/Form';
import './file-field.scss';
import { FieldWithLabel } from '@hoc/withClass';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { FileIcon } from '../../icons/icons/Fileicon';
import { useTranslation } from 'react-i18next';
import { getFileSize } from '@utils/utils';
import { UploadIcon } from '../../icons/icons/UploadIcon';

export interface FormFileFieldProps extends FormFieldProps {
    onErrorMessage?: string;
    name: string;
    acceptedFileExt?: DropzoneOptions['accept'];
}

const ACCEPTED_FILES = {
    'application/xml': ['.xml', '.osm'],
    'application/gzip': ['.gz', '.zip'],
};

const FileField = ({
    name,
    required,
    onChange,
    acceptedFileExt = ACCEPTED_FILES,
    ...props
}: FormFileFieldProps) => {
    const hiddenInputRef = useRef<any>(null);
    const { t } = useTranslation();

    const dropAccepted = useCallback(
        (files, event) => {
            if (!hiddenInputRef.current) {
                return;
            }

            const dt = new DataTransfer();
            files.forEach(f => dt.items.add(f));

            hiddenInputRef.current.files = dt.files;

            hiddenInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
        },
        [onChange]
    );

    const { acceptedFiles, fileRejections, getRootProps, getInputProps, open, isDragActive } =
        useDropzone({
            // noClick: true,
            noKeyboard: true,
            accept: acceptedFileExt,
            maxFiles: 1,
            onDropAccepted: dropAccepted,
        });

    const disableHiddenFieldEvents = useCallback(event => {
        event.stopPropagation();
        event.preventDefault();
    }, []);

    return (
        <div className="file-field-md">
            <input
                type="file"
                name={name}
                required={required}
                ref={hiddenInputRef}
                onClick={disableHiddenFieldEvents}
                disabled={props.disabled}
                accept={Object.values(acceptedFileExt).flat().join(',')}
                style={{ display: 'none' }}
            />
            <div className="dnd-container" {...getRootProps()}>
                <input {...getInputProps()} />
                <div className="dnd-message">
                    <UploadIcon />
                    {`${t('forms.file-field-drag-and-drop-msg')} ${Object.values(acceptedFileExt)
                        .flatMap(types => types)
                        .join(', ')}`}
                </div>
            </div>
            <div className="files-list">
                {acceptedFiles.map((file, idx) => {
                    return (
                        <div key={`${file.name}_${idx}`} className="uploaded-file">
                            <FileIcon />
                            <span>
                                {file.name} - {getFileSize(file.size)}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const FileFieldLabeled = FieldWithLabel(FileField);

export { FileFieldLabeled as Labeled, FileField as Field };

export default FileField;
